import React from "react";
import { FileEdit } from "lucide-react";

/**
 * BaseEditButton — QUESTIONNAIRE-2.0-A-S2-γ-A (stand-alone).
 *
 * Affordance to open a Base-Edit dialog for a single i18n base-lang field
 * (title/description/label/help/section/option_label in EN/ES/ZH).
 *
 * NOT yet wired into the Builder — γ-B will mount it next to each StatusChip
 * when `langIsBase === true`. This component is intentionally side-effect
 * free: it only emits `onClick(path)` and lets the parent decide.
 *
 * Visual rules (per γ analysis §9.1):
 *   - Compact pill, secondary tone (sky-700 / hover sky-50).
 *   - Pencil icon + short label "PATCH base".
 *   - Disabled when `disabled === true`; tooltip surfaces `disabledReason`.
 *   - Test ID derived from `path` for Playwright drivers.
 *
 * Props (all optional except path):
 *   path           Full ts_path used for ID derivation. Required.
 *   onClick        (path) => void. Called when admin clicks. Defaults to no-op.
 *   disabled       Boolean. Hard-disables the button.
 *   disabledReason String. Surfaced in title/tooltip when disabled.
 *   label          Optional override for the visible text. Default "PATCH base".
 *   testId         Optional override for `data-testid`.
 *   compact        When true, render the icon only (no label).
 *   className      Extra Tailwind classes appended to the default styling.
 */
const BaseEditButton = ({
  path,
  onClick = () => {},
  disabled = false,
  disabledReason = "",
  label = "PATCH base",
  testId,
  compact = false,
  className = "",
}) => {
  const slug = (path || "unknown").replace(/[^A-Za-z0-9_]/g, "_");
  const tid = testId || `i18n-base-edit-btn-${slug}`;
  const title = disabled
    ? disabledReason || "Disabled"
    : "Edit base text · triggers translation cascade when reviewed siblings exist";

  const handleClick = () => {
    if (disabled) return;
    onClick(path);
  };

  return (
    <button
      type="button"
      data-testid={tid}
      data-disabled={disabled ? "true" : "false"}
      onClick={handleClick}
      disabled={disabled}
      title={title}
      className={`inline-flex items-center gap-1 rounded-md border border-sky-300 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors
        ${disabled
          ? "text-slate-400 cursor-not-allowed opacity-60"
          : "text-sky-700 hover:bg-sky-50 hover:border-sky-500 cursor-pointer"}
        ${className}`}
    >
      <FileEdit className="w-3 h-3" aria-hidden />
      {!compact && <span data-testid={`${tid}-label`}>{label}</span>}
    </button>
  );
};

export default BaseEditButton;
